import type { UserInput, UserTeam } from '@skop-app/lib-domain/types'

import { isAcceptedOrganizationMembership } from '@/features/team-selection/lib/resolveActiveTeamId'

import {
  hasCompletedOnboarding,
  hasStartedOnboarding,
} from './onboarding.persistence'

export type OrganizationMembershipForOnboarding = UserTeam & {
  organizationStatus?: string | null
}

export type IsOnboardingRequiredParams = {
  isClient: boolean
  isLoadingUser: boolean
  user: UserInput | undefined
  organizations: OrganizationMembershipForOnboarding[] | undefined
}

export const hasAcceptedRealTeamMembership = (
  organizations: OrganizationMembershipForOnboarding[] | undefined,
): boolean =>
  organizations?.some(
    (membership) =>
      isAcceptedOrganizationMembership(membership) &&
      membership.organizationStatus !== 'SOLO',
  ) ?? false

export function isOnboardingRequired(
  params: IsOnboardingRequiredParams,
): boolean {
  if (!params.isClient) return false
  if (params.isLoadingUser) return false
  if (!params.user?.id) return false

  if (hasCompletedOnboarding(params.user.preferences)) {
    return false
  }

  if (params.organizations === undefined) {
    return false
  }

  if (
    !hasStartedOnboarding(params.user.preferences) &&
    hasLegacyManageActivity(params)
  ) {
    return false
  }

  return true
}

export const hasPendingOrganizationInvitation = (
  organizations: OrganizationMembershipForOnboarding[] | undefined,
): boolean =>
  organizations?.some((membership) => membership.status === 'PENDING') ?? false

/**
 * Incomplete onboarding can switch to an invitation at any time, including
 * after a failed acceptance or a reload.
 */
export const isInviteeOnboardingEligible = (
  user: UserInput | undefined,
  organizations: OrganizationMembershipForOnboarding[] | undefined,
): boolean =>
  hasPendingOrganizationInvitation(organizations) &&
  !hasCompletedOnboarding(user?.preferences)

const hasLegacyManageActivity = (
  params: IsOnboardingRequiredParams,
): boolean => {
  if (hasAcceptedRealTeamMembership(params.organizations)) return true

  const shops = (params.user as { shop?: unknown[] } | undefined)?.shop
  if (Array.isArray(shops) && shops.length > 0) return true

  return false
}
